/* global axios */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import api from '../services/apiService';

function RevenueForm() {
  // Filter (Zeitraum & optionale Filter)
  const [startDate, setStartDate]       = useState('');
  const [endDate, setEndDate]           = useState('');
  const [productGroup, setProductGroup] = useState('');
  const [itemId, setItemId]             = useState(''); // optional (ein einzelnes Gerät)
  const [itemType, setItemType]         = useState(''); // Artikelart-Filter
  const INSURANCE_COMBINED = '__INSURANCE_COMBINED__';

  // ⚠️ BOOL-Filter: '' = egal, 'true' = nur Fremdartikel, 'false' = nur nicht-Fremdartikel
  const [foreignArticle, setForeignArticle] = useState('');
  const [storageLocationId, setStorageLocationId] = useState('');

  // Gruppierung & Anzeige
  const [groupBy, setGroupBy]     = useState('productGroup'); // 'productGroup' | 'item' | 'itemType'

  // Options
  const [productGroupOptions, setProductGroupOptions] = useState([]);
  const [itemOptions, setItemOptions]                 = useState([]);
  const [storageLocationOptions, setStorageLocationOptions] = useState([]);

  // Auswahl (für react-select)
  const selectedPG = useMemo(
    () => productGroup ? productGroupOptions.find(o => String(o.value) === String(productGroup)) ?? null : null,
    [productGroup, productGroupOptions]
  );

  // Artikelart-Optionen dynamisch aus Items ableiten
  const itemTypeOptions = useMemo(() => {
    const set = new Set(itemOptions.map(i => i.itemTypeLower).filter(Boolean));
    const base = Array.from(set).sort();
    const opts = base.map(v => ({ value: v, label: v }));
    if (set.has('insurance') || set.has('insurancefixed')) {
      opts.unshift({ value: INSURANCE_COMBINED, label: 'insurance (inkl. fixed)' });
    }
    return [{ value: '', label: 'Alle Artikelarten' }, ...opts];
  }, [itemOptions]);

  // neu: alle Artikel, optional nach Produktgruppe/Artikelart/foreignArticle (BOOL) gefiltert
  const filteredItems = useMemo(() => {
    let list = productGroup
      ? itemOptions.filter(i => i.productGroupId === String(productGroup))
      : itemOptions;

    if (itemType) {
      if (itemType === INSURANCE_COMBINED) {
        list = list.filter(i => i.itemTypeLower === 'insurance' || i.itemTypeLower === 'insurancefixed');
      } else {
        const it = String(itemType).toLowerCase();
        list = list.filter(i => i.itemTypeLower === it);
      }
    }

    // BOOL-Filter (Dropdown)
    if (foreignArticle !== '') {
      const want = (foreignArticle === 'true');
      list = list.filter(i => Boolean(i.foreignArticle) === want);
    }

    if (storageLocationId) {
      list = list.filter(i => i.storageLocationId === String(storageLocationId));
    }

    return list;
  }, [itemOptions, productGroup, itemType, foreignArticle, storageLocationId]);

  const selectedItem = useMemo(
    () => itemId ? filteredItems.find(o => String(o.value) === String(itemId)) ?? null : null,
    [itemId, filteredItems]
  );

  // Daten + UI
  const [rows, setRows]       = useState([]);   // vom Server (bereits gruppiert)
  const [total, setTotal]     = useState(0);    // Gruppen-Gesamt
  const [pages, setPages]     = useState(1);
  const [sumDisplay, setSumDisplay] = useState(0); // Netto/Brutto gesamt (vom Backend entschieden)
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg]   = useState('');

  // Pagination (serverseitig)
  const [page, setPage]   = useState(1);
  const [pageSize]        = useState(100);

  // ---------------------------
  // Optionen laden
  // ---------------------------
  useEffect(() => {
    api.get('/product-groups')
      .then(({ data }) => {
        const opts = (Array.isArray(data) ? data : []).map(pg => ({
          value: String(pg.id),
          label: pg.name,
        }));
        setProductGroupOptions(opts);
      })
      .catch(err => console.error('Fehler beim Laden der Produktgruppen:', err));
  }, []);

  useEffect(() => {
    api.get('/items')
      .then(({ data }) => {
        const opts = (Array.isArray(data) ? data : []).map(i => ({
          id: i.id,
          value: String(i.id),
          label: `${i.name} (ID: ${i.id})`,
          itemType: i.itemType,
          itemTypeLower: (i.itemType || '').toLowerCase(),
          productGroupId: String(i.productGroupId ?? ''),
          foreignArticle: Boolean(i.foreignArticle), // 👈 BOOL
          storageLocationId: String(i.storageLocationId ?? ''),
        }));
        setItemOptions(opts);
      })
      .catch(err => console.error('Fehler beim Laden der Items:', err));
  }, []);

  useEffect(() => {
    api.get('/storage-locations')
      .then(({ data }) => {
        const opts = (Array.isArray(data) ? data : []).map(location => ({
          value: String(location.id),
          label: `${location.name}${location.isActive === false ? ' (inaktiv)' : ''}`,
        }));
        setStorageLocationOptions(opts);
      })
      .catch(err => console.error('Fehler beim Laden der Lagerorte:', err));
  }, []);

  // ---------------------------
  // Daten laden (Rechnungen)
  // ---------------------------
  const buildParams = useCallback(() => {
    const p = {};
    if (startDate)    p.dateFrom     = startDate;
    if (endDate)      p.dateTo       = endDate;
    if (productGroup) p.productGroup = productGroup;
    if (itemId)       p.itemId       = itemId;
    if (itemType) {
      p.itemType = (itemType === INSURANCE_COMBINED) ? INSURANCE_COMBINED : String(itemType).toLowerCase();
    }
    // nur anhängen, wenn gesetzt
    if (foreignArticle !== '') p.foreignArticle = foreignArticle; // 'true' | 'false'
    if (storageLocationId) p.storageLocationId = storageLocationId;
    if (groupBy)      p.groupBy      = groupBy;
    p.page = page;
    p.pageSize = pageSize;
    return p;
  }, [startDate, endDate, productGroup, itemId, itemType, foreignArticle, storageLocationId, groupBy, page, pageSize]);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) {
      setErrMsg('Bitte Start- und Enddatum wählen!');
      setRows([]); setTotal(0); setPages(1); setSumDisplay(0);
      return;
    }
    setLoading(true);
    setErrMsg('');
    try {
      const params = buildParams();
      const { data } = await api.get('/reports/revenue/invoices', { params });
      setTotal(Number(data.total || 0));
      setPages(Math.max(1, Number(data.pages || 1)));
      setRows(Array.isArray(data.data) ? data.data : []);
      setSumDisplay(Number(data?.sumAll?.sumDisplay || 0));
    } catch (e) {
      console.error('Revenue (Rechnungen) laden fehlgeschlagen:', e);
      setErrMsg('Fehler beim Abrufen des Umsatzberichts (Basis: Rechnungen)');
      setRows([]); setTotal(0); setPages(1); setSumDisplay(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams, startDate, endDate]);

  // Initial kein Auto-Load; erst nach Submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (page !== 1) setPage(1);
    else fetchData();
  };

  // Re-Fetch bei Seitenwechsel
  useEffect(() => {
    if (!startDate || !endDate) return;
    fetchData();
  }, [page, fetchData, startDate, endDate]);

  // Bei Filterwechsel Seite zurück (aber noch nicht laden, bis Submit)
  useEffect(() => {
    setPage(1);
  }, [productGroup, itemId, itemType, foreignArticle, storageLocationId, groupBy, pageSize]); // 👈 Filter ergänzt

  // Gruppierung für Maschinen: PG-Header + Zwischensummen
  const groupedByPG = useMemo(() => {
    if (groupBy !== 'item') return null;
    const map = new Map();
    for (const r of rows) {
      const key = r.pgName || 'Unbekannte PG';
      if (!map.has(key)) map.set(key, { pgName: key, items: [], subtotal: 0, invoiceCount: 0 });
      const g = map.get(key);
      g.items.push(r);
      g.subtotal += Number(r.display || 0);
      g.invoiceCount += Number(r.invoiceCount || 0);
    }
    return Array.from(map.values()).sort((a, b) => a.pgName.localeCompare(b.pgName));
  }, [rows, groupBy]);

  // Gruppierung für Artikelart: Header (Artikelart) + Artikelzeilen + Zwischensumme
  const groupedByItemType = useMemo(() => {
    if (groupBy !== 'itemType') return null;
    const map = new Map();
    for (const r of rows) {
      const key = r.groupName || 'Unbekannt'; // vom Backend geliefert (itemType)
      if (!map.has(key)) map.set(key, { itemType: key, items: [], subtotal: 0, invoiceCount: 0 });
      const g = map.get(key);
      g.items.push(r); // r.keyId (Item-ID), r.keyName (Artikel)
      g.subtotal += Number(r.display || 0);
      g.invoiceCount += Number(r.invoiceCount || 0);
    }
    return Array.from(map.values()).sort((a, b) => a.itemType.localeCompare(b.itemType));
  }, [rows, groupBy]);

  // CSV (aus Rechnungen)
  const handleDownloadCSV = async () => {
    if (!startDate || !endDate) {
      setErrMsg('Bitte Start- und Enddatum wählen!');
      return;
    }
    try {
      const params = { ...buildParams(), format: 'csv' };
      const response = await api.get('/reports/revenue/invoices', { params, responseType: 'blob' });
      const filename = `revenue_invoices_${groupBy}.csv`;
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV-Download fehlgeschlagen:', e);
      setErrMsg('CSV-Download fehlgeschlagen');
    }
  };

  const handleDownloadPDF = async () => {
    if (!startDate || !endDate) {
      setErrMsg('Bitte Start- und Enddatum wÃ¤hlen!');
      return;
    }

    const pdfTab = window.open('', '_blank');

    try {
      const params = { ...buildParams(), format: 'pdf' };
      const response = await api.get('/reports/revenue/invoices', {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);

      if (pdfTab) {
        pdfTab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch (error) {
      if (pdfTab) pdfTab.close();
      console.error('Revenue-PDF konnte nicht geladen werden:', error);
      setErrMsg('Revenue-PDF konnte nicht erstellt werden.');
    }
  };

  const gotoPrev = () => setPage(p => Math.max(1, p - 1));
  const gotoNext = () => setPage(p => Math.min(pages || 1, p + 1));

  // Spaltenüberschriften je Modus
  const col1Label = groupBy === 'item'
    ? 'Item-ID'
    : groupBy === 'itemType'
    ? 'Item-ID'
    : 'PG-ID';

  const col2Label = groupBy === 'item'
    ? 'Maschine'
    : groupBy === 'itemType'
    ? 'Artikel'
    : 'Produktgruppe';
    

  return (
    <div style={{ maxWidth: 1100, margin: 'auto' }}>
      <Helmet>
        <title>Bericht: Maschinen Umsatz</title>
        <meta name="Revenue Report" content="Revenue / DB Report (Invoices)" />
      </Helmet>

      <h2>📊 Maschinen Umsatz</h2>

      {/* Filterleiste */}
      <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          {/* Zeitraum */}
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Startdatum</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Enddatum</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: '100%' }} />
          </div>

          {/* Produktgruppe */}
          <div style={{ flex: '1 1 260px', minWidth: 260 }}>
            <label htmlFor="pgSelect" style={{ display: 'inline-block', marginBottom: 4 }}></label>
            <Select
              inputId="pgSelect"
              isClearable
              placeholder="Produktgruppe wählen…"
              options={productGroupOptions}
              value={selectedPG}
              onChange={(opt) => {
                setProductGroup(opt ? String(opt.value) : '');
                setItemId('');
                setPage(1);
              }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPlacement="auto"
              menuPosition="fixed"
              styles={{
                control:   (base) => ({ ...base, minHeight: 34, width: '100%', marginBottom: 16 }),
                container: (base) => ({ ...base, marginTop: -5 }),
                menuPortal:(base) => ({ ...base, zIndex: 9999 }),
                menu:      (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>

          {/* Fremdartikel (BOOL) */}
<div
  style={{
    flex: '0 0 220px',
    minWidth: 100,
    display: 'flex',
    flexDirection: 'column',
  }}
>
  <label htmlFor="faSelect" style={{ display: 'inline-block', marginBottom: 4 }}>
    
  </label>
  <select
    id="faSelect"
    value={foreignArticle}
    onChange={(e) => { setForeignArticle(e.target.value); setPage(1); }}
    style={{ width: 180, height: 38, margin: '-5px 0 16px 0' }}
  >
    <option value="">Eigen/Fremdartikel</option>
    <option value="true">nur Fremdartikel (true)</option>
    <option value="false">nur Eigenartikel (false)</option>
  </select>
</div>

        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginTop: 12 }}>

          {/* Lagerort */}
          <div
            style={{
              flex: '0 0 220px',
              minWidth: 100,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <label htmlFor="storageLocationSelect" style={{ display: 'inline-block', marginBottom: 4 }}></label>
            <select
              id="storageLocationSelect"
              value={storageLocationId}
              onChange={(e) => {
                setStorageLocationId(e.target.value);
                setItemId('');
                setPage(1);
              }}
              style={{ width: '100%', marginLeft: 0, marginRight: 0 }}
            >
              <option value="">Alle Lagerorte</option>
              {storageLocationOptions.map(location => (
                <option key={location.value} value={location.value}>{location.label}</option>
              ))}
            </select>
          </div>

         {/* Artikelart (Filter) */}
<div
  style={{
    flex: '1 1 160px',     // ⬅️ gleiche Breite wie Startdatum
    minWidth: 160,         // ⬅️ exakt identisch
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start', // ⬅️ sorgt für echte Linksbündigkeit
  }}
>
  <label
    htmlFor="itemTypeSelect"
    style={{ display: 'inline-block', marginBottom: 4, marginLeft: 0 }}
  >
    
  </label>
  <select
    id="itemTypeSelect"
    value={itemType}
    onChange={(e) => { setItemType(e.target.value); setPage(1); }}
    style={{
      width: '100%',        // ⬅️ statt 80%
      marginLeft: 0,        // ⬅️ kein Versatz
      marginRight: 0,
      boxSizing: 'border-box',
    }}
  >
    {itemTypeOptions.map(o => (
      <option key={o.value || 'all'} value={o.value}>{o.label}</option>
    ))}
  </select>
</div>

          {/* Artikel (optional) */}
          <div style={{ flex: '1 1 320px', minWidth: 260 }}>
            <label htmlFor="itemSelect" style={{ display: 'inline-block', marginBottom: 4 }}></label>
            <Select
              inputId="itemSelect"
              isClearable
              placeholder={productGroup ? 'Artikel in dieser PG…' : 'Alle Artikel…'}
              options={filteredItems}
              value={selectedItem}
              onChange={(opt) => {
                setItemId(opt ? String(opt.value) : '');
                setPage(1);
              }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPlacement="auto"
              menuPosition="fixed"
              styles={{
                control:   (base) => ({ ...base, minHeight: 42, height: 42, width: '100%', marginBottom: 15 }),
                container: (base) => ({ ...base, marginTop: 10 }),
                menuPortal:(base) => ({ ...base, zIndex: 9999 }),
                menu:      (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>

          {/* Gruppierung */}
          <div style={{ flex: '1 1 220px', minWidth: 220 }}>
            <label>Gruppieren nach</label>
            <select
              value={groupBy}
              onChange={(e) => { setGroupBy(e.target.value); setPage(1); }}
              style={{ width: '100%', marginLeft: 0, marginRight: 0 }}
            >
              <option value="productGroup">Produktgruppe</option>
              <option value="item">Maschine</option>
              <option value="itemType">Artikelart</option>
            </select>
         
          </div>

          {/* Aktionen rechts */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
            <button type="button" onClick={handleDownloadPDF} disabled={loading}>PDF drucken</button>
            <button type="button" onClick={handleDownloadCSV} disabled={loading}>CSV (Rechnungen)</button>
          </div>
        </div>
      </form>

      {errMsg && <p style={{ color: 'red' }}>{errMsg}</p>}

      {/* Summary-Bar – eine Zeile */}
      <div style={{ marginBottom: 8 }}>
        <strong>Gesamt:</strong> {total} Gruppen &nbsp;|&nbsp;
        <strong>Summe:</strong>{' '}
        {Number(sumDisplay).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
      </div>

      {/* Pagination-Toolbar – direkt oberhalb der Tabelle */}
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
        <button onClick={gotoPrev} disabled={loading || page <= 1}>Zurück</button>
        <span>Seite {page} / {pages || 1}</span>
        <button onClick={gotoNext} disabled={loading || page >= (pages || 1)}>Weiter</button>
      </div>

      {/* Tabelle */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fffefeff' }}>
              <th style={{ textAlign: 'left', padding: 8 }}>{col1Label}</th>
              <th style={{ textAlign: 'left', padding: 8 }}>{col2Label}</th>
              <th style={{ textAlign: 'right', padding: 8 }}># Rechnungen</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Summe (€)</th>
            </tr>
          </thead>

          <tbody>
            {/* Keine Daten */}
            {rows.length === 0 && !loading && (
              <tr><td colSpan="4" style={{ padding: 12, textAlign: 'center' }}>Keine Daten</td></tr>
            )}

            {/* Gruppiert nach Maschine => Produktgruppen-Header + Items + Zwischensumme */}
            {groupBy === 'item' && groupedByPG && groupedByPG.map(group => (
              <React.Fragment key={`pg-${group.pgName}`}>
                {/* Gruppen-Header */}
                <tr style={{ background: '#f6f6f6', borderTop: '1px solid #ddd' }}>
                  <td colSpan={4} style={{ padding: 8, fontWeight: 600 }}>
                    Produktgruppe: {group.pgName}
                  </td>
                </tr>

                {/* Maschinen-Zeilen */}
                {group.items.map(r => (
                  <tr key={`${r.keyId}-${r.keyName}`} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: 8 }}>{r.keyId ?? '—'}</td>
                    <td style={{ padding: 8 }}>{r.keyName ?? '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{r.invoiceCount}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {Number(r.display).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}

                {/* Zwischensumme je PG (nur Seitensumme) */}
                <tr style={{ borderTop: '2px solid #ddd', background: '#fafafa' }}>
                  <td colSpan={2} style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>Zwischensumme</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{group.invoiceCount}</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>
                    {group.subtotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </React.Fragment>
            ))}

            {/* Gruppiert nach Produktgruppe => flach */}
            {groupBy === 'productGroup' && rows.map(r => (
              <tr key={`${r.keyId}-${r.keyName}`} style={{ borderTop: '1px solid #ddd' }}>
                <td style={{ padding: 8 }}>{r.keyId ?? '—'}</td>
                <td style={{ padding: 8 }}>{r.keyName ?? '—'}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{r.invoiceCount}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>
                  {Number(r.display).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                </td>
              </tr>
            ))}

            {/* Gruppiert nach Artikelart => Artikelart-Header + Artikel-Zeilen + Zwischensumme */}
            {groupBy === 'itemType' && groupedByItemType && groupedByItemType.map(group => (
              <React.Fragment key={`it-${group.itemType}`}>
                {/* Artikelart-Header */}
                <tr style={{ background: '#f6f6f6', borderTop: '1px solid #ddd' }}>
                  <td colSpan={4} style={{ padding: 8, fontWeight: 600 }}>
                    Artikelart: {group.itemType}
                  </td>
                </tr>

                {/* Artikel-Zeilen */}
                {group.items.map(r => (
                  <tr key={`${r.keyId}-${r.keyName}`} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: 8 }}>{r.keyId ?? '—'}</td>
                    <td style={{ padding: 8 }}>{r.keyName ?? '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{r.invoiceCount}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {Number(r.display).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}

                {/* Zwischensumme je Artikelart (nur Seitensumme) */}
                <tr style={{ borderTop: '2px solid #ddd', background: '#fafafa' }}>
                  <td colSpan={2} style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>Zwischensumme</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{group.invoiceCount}</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>
                    {group.subtotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {loading && <p style={{ marginTop: 8 }}>Lade…</p>}
    </div>
  );
}

export default RevenueForm;
